/** Web-Audio + optionale MP3s unter /sfx (Mixkit) */

let ctx: AudioContext | null = null;
let unlocked = false;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  return ctx;
}

export function unlockAudio() {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") void c.resume();
  unlocked = true;
}

function tone(
  freq: number,
  start: number,
  dur: number,
  type: OscillatorType,
  gain: number,
) {
  const c = getCtx();
  if (!c || !unlocked) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  g.gain.setValueAtTime(0.0001, start);
  g.gain.exponentialRampToValueAtTime(Math.max(0.001, gain), start + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(g);
  g.connect(c.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

export type SoundKind =
  | "alarm"
  | "dispatch"
  | "incident"
  | "arrive"
  | "resolve"
  | "proximity"
  | "radio"
  | "sprechwunsch"
  | "status5"
  | "phone"
  | "tow"
  | "support"
  | "chatter"
  | "skill"
  | "purchase"
  | "ui";

const VOL_KEY = "bda-sfx-vol";
const PROX_KEY = "bda-proximity-sfx";
const CHATTER_KEY = "bda-chatter-sfx";

let lastChatterAt = 0;

export function getSfxVolume(): number {
  const v = Number(localStorage.getItem(VOL_KEY) ?? "0.55");
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.55;
}

export function setSfxVolume(v: number) {
  localStorage.setItem(VOL_KEY, String(Math.min(1, Math.max(0, v))));
}

export function getProximitySfxEnabled(): boolean {
  const v = localStorage.getItem(PROX_KEY);
  return v !== "0";
}

export function setProximitySfxEnabled(on: boolean) {
  localStorage.setItem(PROX_KEY, on ? "1" : "0");
}

export function getChatterSfxEnabled(): boolean {
  const v = localStorage.getItem(CHATTER_KEY);
  return v !== "0";
}

export function setChatterSfxEnabled(on: boolean) {
  localStorage.setItem(CHATTER_KEY, on ? "1" : "0");
}

const SFX_FILE: Partial<Record<SoundKind, string>> = {
  sprechwunsch: "/sfx/radio-call.mp3",
  radio: "/sfx/radio-call.mp3",
  status5: "/sfx/status5.mp3",
  phone: "/sfx/phone-ring.mp3",
  tow: "/sfx/tow.mp3",
  support: "/sfx/support.mp3",
  chatter: "/sfx/radio-chatter.mp3",
};

function playFile(url: string, vol: number): boolean {
  try {
    const a = new Audio(url);
    a.volume = Math.min(1, vol * 0.85);
    void a.play().catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}

export function playSound(kind: SoundKind) {
  if (kind === "proximity" && !getProximitySfxEnabled()) return;
  unlockAudio();
  const c = getCtx();
  if (!c) return;
  const vol = getSfxVolume();
  if (vol <= 0.01) return;
  const t0 = c.currentTime;

  const file = SFX_FILE[kind];
  if (file && playFile(file, vol)) {
    // kurze Synth-Unterlage für Charakter
    if (kind === "tow" || kind === "support") {
      tone(180, t0, 0.08, "triangle", 0.02 * vol);
    }
    return;
  }

  if (kind === "alarm" || kind === "dispatch") {
    for (let i = 0; i < 4; i++) {
      const t = t0 + i * 0.18;
      tone(880, t, 0.14, "sawtooth", 0.08 * vol);
      tone(660, t + 0.09, 0.14, "sawtooth", 0.07 * vol);
    }
    tone(520, t0, 0.7, "triangle", 0.04 * vol);
    return;
  }

  if (kind === "incident") {
    tone(620, t0, 0.08, "triangle", 0.055 * vol);
    tone(930, t0 + 0.1, 0.12, "triangle", 0.06 * vol);
    tone(1240, t0 + 0.24, 0.18, "sine", 0.05 * vol);
    return;
  }

  if (kind === "arrive") {
    tone(440, t0, 0.1, "sine", 0.05 * vol);
    tone(660, t0 + 0.1, 0.15, "sine", 0.05 * vol);
    return;
  }

  if (kind === "resolve") {
    tone(520, t0, 0.12, "sine", 0.04 * vol);
    tone(650, t0 + 0.12, 0.14, "sine", 0.045 * vol);
    tone(780, t0 + 0.26, 0.22, "triangle", 0.05 * vol);
    return;
  }

  if (kind === "proximity") {
    tone(620, t0, 0.18, "sawtooth", 0.028 * vol);
    tone(480, t0 + 0.16, 0.2, "sawtooth", 0.024 * vol);
    return;
  }

  if (kind === "sprechwunsch" || kind === "radio") {
    tone(780, t0, 0.06, "square", 0.06 * vol);
    tone(520, t0 + 0.08, 0.1, "square", 0.05 * vol);
    tone(780, t0 + 0.2, 0.12, "square", 0.045 * vol);
    tone(980, t0 + 0.34, 0.08, "triangle", 0.035 * vol);
    return;
  }

  if (kind === "status5") {
    tone(1400, t0, 0.04, "square", 0.045 * vol);
    tone(1100, t0 + 0.06, 0.05, "square", 0.04 * vol);
    tone(1400, t0 + 0.13, 0.06, "sine", 0.035 * vol);
    return;
  }

  if (kind === "tow" || kind === "support") {
    tone(220, t0, 0.12, "sawtooth", 0.05 * vol);
    tone(330, t0 + 0.14, 0.16, "triangle", 0.045 * vol);
    tone(440, t0 + 0.32, 0.2, "sine", 0.04 * vol);
    return;
  }

  if (kind === "phone") {
    tone(440, t0, 0.12, "sine", 0.05 * vol);
    tone(440, t0 + 0.22, 0.12, "sine", 0.05 * vol);
    tone(440, t0 + 0.44, 0.12, "sine", 0.045 * vol);
    return;
  }

  if (kind === "skill") {
    tone(440, t0, 0.1, "sine", 0.05 * vol);
    tone(554, t0 + 0.1, 0.12, "sine", 0.055 * vol);
    tone(659, t0 + 0.22, 0.14, "triangle", 0.06 * vol);
    tone(880, t0 + 0.36, 0.22, "triangle", 0.065 * vol);
    tone(1108, t0 + 0.52, 0.28, "sine", 0.05 * vol);
    return;
  }

  if (kind === "purchase") {
    // kurzes „Ka-ching“
    tone(980, t0, 0.06, "triangle", 0.06 * vol);
    tone(1310, t0 + 0.07, 0.1, "sine", 0.07 * vol);
    tone(1660, t0 + 0.16, 0.18, "triangle", 0.055 * vol);
    tone(2200, t0 + 0.28, 0.12, "sine", 0.03 * vol);
    return;
  }

  if (kind === "ui") {
    tone(720, t0, 0.04, "sine", 0.028 * vol);
    tone(880, t0 + 0.04, 0.05, "triangle", 0.022 * vol);
    return;
  }
}

/** Leises Funk-Gebimmel nahe Einsatzstelle — gedrosselt, keine Sirene */
export function playRadioChatterNearScene() {
  if (!getChatterSfxEnabled()) return;
  const now = Date.now();
  if (now - lastChatterAt < 12_000) return;
  lastChatterAt = now;
  unlockAudio();
  const vol = getSfxVolume();
  if (vol <= 0.01) return;
  if (playFile("/sfx/radio-chatter.mp3", vol * 0.55)) return;

  const c = getCtx();
  if (!c || !unlocked) return;
  const t0 = c.currentTime;
  const bufferSize = c.sampleRate * 0.35;
  const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = 920;
  filter.Q.value = 0.7;
  const g = c.createGain();
  g.gain.value = 0.022 * vol;
  src.connect(filter);
  filter.connect(g);
  g.connect(c.destination);
  src.start(t0);
  tone(640, t0 + 0.05, 0.04, "square", 0.012 * vol);
  tone(520, t0 + 0.14, 0.05, "square", 0.01 * vol);
}
